import React, { useState } from 'react';
import PetDisplay from './PetDisplay';
import StatBars from './StatBars';

const TestStates = () => {
  const [selectedState, setSelectedState] = useState('good');
  
  // All possible states
  const states = [
    'excellent',
    'good',
    'fair',
    'poor',
    'hungry',
    'tired',
    'sad',
    'starving',
    'exhausted',
    'depressed',
    'disconnected'
  ];
  
  // Mock data for each state
  const mockData = {
    'excellent': { hunger: 90, energy: 90, happiness: 90, currentState: 'excellent' },
    'good': { hunger: 75, energy: 75, happiness: 75, currentState: 'good' },
    'fair': { hunger: 60, energy: 60, happiness: 60, currentState: 'fair' },
    'poor': { hunger: 45, energy: 45, happiness: 45, currentState: 'poor' },
    'hungry': { hunger: 30, energy: 65, happiness: 65, currentState: 'hungry' },
    'tired': { hunger: 65, energy: 30, happiness: 65, currentState: 'tired' },
    'sad': { hunger: 65, energy: 65, happiness: 30, currentState: 'sad' },
    'starving': { hunger: 10, energy: 50, happiness: 50, currentState: 'starving' },
    'exhausted': { hunger: 50, energy: 10, happiness: 50, currentState: 'exhausted' },
    'depressed': { hunger: 50, energy: 50, happiness: 10, currentState: 'depressed' },
    'disconnected': { hunger: 20, energy: 20, happiness: 20, currentState: 'disconnected' }
  };
  
  // Messages for each state
  const messages = {
    'starving': "Nostrie is starving! The network needs more notes.",
    'exhausted': "Nostrie is exhausted! Zap activity is very low.",
    'depressed': "Nostrie is depressed. Moscow Time (sats/USD) is falling.",
    'hungry': "Nostrie is getting hungry. Network activity is below normal.",
    'tired': "Nostrie is tired. Zap energy is lower than usual.",
    'sad': "Nostrie is feeling sad. Moscow Time is declining.",
    'excellent': "Nostrie is thriving! The Nostr network is very healthy.",
    'good': "Nostrie is feeling good. The network is in good shape.",
    'fair': "Nostrie is doing okay. Network activity is moderate.",
    'poor': "Nostrie isn't feeling great. The network could use more activity.",
    'disconnected': "Unable to connect to the Nostr network. Please check your connection."
  };

  return (
    <div className="min-h-screen bg-dark-500 py-6 px-4">
      <header className="text-center mb-6">
        <h1 className="text-3xl font-pixel text-primary-400 mb-2">NOSTRIE TEST STATES</h1>
        <p className="text-sm text-accent-400 font-mono">View all possible states of the Nostr Network Pet</p>
        <p className="mt-2">
          <a href="/" className="text-primary-400 hover:text-primary-300 underline font-mono">← Back to Main Page</a>
        </p>
      </header>
      
      <div className="mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">Select a State</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
          {states.map(state => (
            <button
              key={state}
              onClick={() => setSelectedState(state)}
              className={`py-2 px-4 font-pixel text-sm rounded arcade-panel ${
                selectedState === state 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-dark-700 text-primary-300 hover:bg-dark-600'
              }`}
            >
              {state.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      
      <div className="arcade-container mb-6 scanlines screen-glow">
        <PetDisplay state={selectedState} />
        <StatBars stats={mockData[selectedState]} />
        <p className="text-center mt-4 text-sm font-pixel text-accent-400">{messages[selectedState]}</p>
      </div>
      
      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">State Details</h2>
        <div className="bg-dark-800 p-4 rounded font-mono text-sm">
          <h3 className="text-accent-400 mb-2">STATE: <span className="text-primary-300">{selectedState.toUpperCase()}</span></h3>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-gray-400">Hunger: <span className="text-primary-300">{mockData[selectedState].hunger}%</span></p>
              <p className="text-gray-400">Energy: <span className="text-primary-300">{mockData[selectedState].energy}%</span></p>
              <p className="text-gray-400">Happiness: <span className="text-primary-300">{mockData[selectedState].happiness}%</span></p>
            </div>
            <div>
              <p className="text-gray-400">Average: <span className="text-primary-300">
                {Math.round((mockData[selectedState].hunger + mockData[selectedState].energy + mockData[selectedState].happiness) / 3)}%
              </span></p>
              <p className="text-gray-400">Status: <span className={`
                ${selectedState === 'excellent' ? 'text-success-400' : ''}
                ${selectedState === 'good' ? 'text-success-300' : ''}
                ${selectedState === 'fair' ? 'text-warning-300' : ''}
                ${selectedState === 'poor' ? 'text-warning-400' : ''}
                ${['hungry', 'tired', 'sad'].includes(selectedState) ? 'text-warning-500' : ''}
                ${['starving', 'exhausted', 'depressed', 'disconnected'].includes(selectedState) ? 'text-danger-500' : ''}
              `}>{selectedState.toUpperCase()}</span></p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">State Triggers</h2>
        <div className="bg-dark-800 p-4 rounded font-mono text-sm">
          <ul className="space-y-2">
            <li><span className="text-primary-300">excellent:</span> All stats above 85%</li>
            <li><span className="text-primary-300">good:</span> All stats above 65%</li>
            <li><span className="text-primary-300">fair:</span> All stats above 50%</li>
            <li><span className="text-primary-300">poor:</span> Any stat below 50%</li>
            <li><span className="text-primary-300">hungry:</span> Hunger below 35%</li>
            <li><span className="text-primary-300">tired:</span> Energy below 35%</li>
            <li><span className="text-primary-300">sad:</span> Happiness below 35%</li>
            <li><span className="text-primary-300">starving:</span> Hunger below 15%</li>
            <li><span className="text-primary-300">exhausted:</span> Energy below 15%</li>
            <li><span className="text-primary-300">depressed:</span> Happiness below 15%</li>
            <li><span className="text-primary-300">disconnected:</span> No connection to Nostr relays</li>
          </ul>
        </div>
      </div>
      
      <footer className="text-center text-gray-500 text-xs font-mono mt-8 mb-4">
        <p>Nostrie monitors the health of the Nostr network in real-time.</p>
        <p className="mt-2">
          © {new Date().getFullYear()} Nostrie Project
          <span className="mx-2">|</span>
          <a href="/raw-data-log" className="text-primary-400 hover:text-primary-300">Log</a>
        </p>
      </footer>
    </div>
  );
};

export default TestStates; 